<template>
  <v-container>
    <v-row justify="start" no-gutters class="my-2">
      <v-btn exact text :to="{ name: prevRoute }">
        <v-icon small left>fa-chevron-left</v-icon>Retour
      </v-btn>
    </v-row>
    <v-card class="mb-2">
      <v-card-text class="pb-0">
        <DeliverySlipFields
          :date.sync="date"
          :time.sync="time"
          v-bind.sync="deliverySlip"
          :valid.sync="fieldsValid"
        ></DeliverySlipFields>
      </v-card-text>
    </v-card>
    <v-card>
      <v-card-text>
        <v-row class="mb-2" align="center" dense>
          <v-col cols="12" sm="6">
            <DeliverySlipOrderTemplateSelect
              v-model="templateId"
            ></DeliverySlipOrderTemplateSelect>
          </v-col>
        </v-row>
        <v-row
          align="center"
          dense
          v-if="template && template.sales_target !== null"
        >
          <v-col cols="12" sm="6">
            <j-decimal-input
              filled
              dense
              class="no-before"
              suffix="$"
              label="Estimation des ventes"
              v-model="salesTargetValue"
            ></j-decimal-input>
          </v-col>
          <v-col cols="12" sm="6">
            <j-decimal-input
              filled
              dense
              class="no-before"
              label="Nombre de jour sur cette commande**"
              v-model="numberOfDaysValue"
            ></j-decimal-input>
          </v-col>
        </v-row>
        <DeliverySlipCreatorGrid
          v-if="template"
          :items="getFilteredItems"
          :date="date"
          :sales_target="template.sales_target"
        ></DeliverySlipCreatorGrid>
      </v-card-text>
    </v-card>
    <v-card class="mt-2">
      <v-card-text>
        <v-textarea
          filled
          dense
          class="no-before"
          placeholder="Commentaires"
          hide-details
          v-model="deliverySlip.comments"
        ></v-textarea>
      </v-card-text>
    </v-card>
    <v-row align="center" justify="end" no-gutters class="mt-2">
      <v-btn
        color="success"
        @click="saveDeliverySlip"
        :disabled="!fieldsValid"
        :block="$vuetify.breakpoint.xsOnly"
        :large="$vuetify.breakpoint.smAndDown"
      >
        <v-icon left small>fa-save</v-icon>Enregistrer
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
import moment from "moment";
import { api } from "@/api";
import { mapGetters } from "vuex";
import { round10 } from "round10";
import { mappers } from "@/mappers";
import {
  DeliverySlipFields,
  DeliverySlipOrderTemplateSelect,
  DeliverySlipCreatorGrid
} from "../components";
export default {
  name: "DeliverySlipCreator",
  components: {
    DeliverySlipFields,
    DeliverySlipOrderTemplateSelect,
    DeliverySlipCreatorGrid
  },
  async beforeRouteEnter(to, from, next) {
    next(async vm => {
      vm.prevRoute = from.name;
      await vm.getInitialData();
    });
  },
  async beforeRouteUpdate() {
    await this.getInitialData();
  },
  computed: {
    ...mapGetters([
      "getProducts",
      "getInventoryRecords",
      "getOrderTemplates",
      "getSuppliers",
      "getStoreId",
      "getRecipes",
      "getSalesEstimates"
    ]),
    getFilteredItems() {
      if (this.deliverySlip.fk_supplier) {
        const supplier = this.getSuppliers.find(
          x => x.id_fournisseur === this.deliverySlip.fk_supplier
        );
        if (supplier && supplier.data && supplier.data.supplier_tag !== null) {
          return this.items.filter(
            x =>
              x.data &&
              x.data.tags
                .map(t => t.toLowerCase())
                .includes(supplier.data.supplier_tag.toLowerCase()) &&
              (x.quantity_minimum > 0 ||
                x.quantity_needed < 0 ||
                x.is_order_by_inventory_link === true)
          );
        }
      }
      return this.items.filter(
        x =>
          x.quantity_minimum > 0 ||
          x.quantity_needed < 0 ||
          x.is_order_by_inventory_link === true
      );
    }
  },
  data() {
    return {
      fieldsValid: false,
      deliverySlip: {
        author: "",
        fk_supplier: null,
        is_deleted: false,
        is_receive: false,
        date: moment().toDate(),
        sales_target: null,
        number_of_days: null,
        comments: null
      },
      salesTargetValue: null,
      numberOfDaysValue: null,
      date: moment().format("YYYY-MM-DD"),
      time: null,
      items: [],
      loading: false,
      template: undefined,
      templateId: null,
      prevRoute: "DeliverySlipIndex"
    };
  },
  methods: {
    async getInitialData() {
      try {
        this.$store.dispatch("slo");
        await this.$store.dispatch("loadExpirations");
        await Promise.all([
          this.$store.dispatch("loadInventoryRecords", {
            store: this.getStoreId,
            latest: true
          }),
          this.$store.dispatch("loadOrderTemplates"),
          this.$store.dispatch("loadProducts"),
          this.$store.dispatch("loadRecipes"),
          this.$store.dispatch("loadSuppliers"),
          this.$store.dispatch("loadSalesEstimates")
        ]);
      } finally {
        this.$store.dispatch("elo");
      }
    },
    async getData() {
      try {
        this.$store.dispatch("slo");
        const time = moment();
        if (time.hours < 11) {
          this.time = moment()
            .hour(5)
            .minute(0)
            .second(0)
            .millisecond(0)
            .toDate()
            .toLocaleTimeString();
        } else if (time.hours >= 16) {
          this.time = moment()
            .hour(18)
            .minute(0)
            .second(0)
            .millisecond(0)
            .toDate()
            .toLocaleTimeString();
        } else {
          this.time = moment()
            .hour(12)
            .minute(0)
            .second(0)
            .millisecond(0)
            .toDate()
            .toLocaleTimeString();
        }
        this.items = [...this.getProducts]
          .filter(
            x =>
              (x.est_intermediaire === false ||
                x.use_inventory_link === true) &&
              x.is_deleted === false
          )
          .map(x => {
            return {
              ...x,
              fk_product: x.id,
              quantity_inventory: this.getQuantity(x),
              quantity_ordered: 0,
              quantity_received: 0,
              orders: this.getOrders(x),
              quantity_minimum:
                this.templateId !== null ? this.getMinimum(x) : null,
              quantity_maximum:
                this.templateId !== null ? this.getMaximum(x) : null,
              quantity_by_sale:
                this.templateId !== null ? this.getBySales(x) : null,
              quantity_needed:
                this.templateId !== null ? this.getNeeded(x) : null,
              is_order_by_inventory_link: false,
              order_by_inventory_link: []
            };
          });
        this.inventoryLinkHandler();
      } finally {
        this.$store.dispatch("elo");
      }
    },
    getQuantity(item) {
      const record = this.getInventoryRecords.find(
        i => i.fk_product === item.id
      );
      if (record) {
        return round10(Number(record.computedQuantity), -2);
      } else {
        return 0;
      }
    },
    getOrders(item) {
      const record = this.getInventoryRecords.find(
        x => x.fk_product === item.id
      );
      if (record) {
        return record.orders;
      } else {
        return [];
      }
    },
    getMinimum(item) {
      if (this.template.quantities !== undefined) {
        const productMinimum = this.template.quantities.find(
          x => x.fk_product === item.id
        );
        if (productMinimum) {
          return round10(Number(productMinimum.quantity), -2);
        } else {
          return 0;
        }
      } else {
        return 0;
      }
    },
    getMaximum(item) {
      if (this.template.quantities !== undefined) {
        const productMinimum = this.template.quantities.find(
          x => x.fk_product === item.id
        );
        if (productMinimum) {
          return round10(Number(productMinimum.quantity_max), -2);
        } else {
          return null;
        }
      } else {
        return null;
      }
    },
    getBySales(item) {
      if (
        this.template.quantities !== undefined &&
        this.template.sales_target !== null
      ) {
        const product = this.template.quantities.find(
          x => x.fk_product === item.id
        );
        if (product) {
          return round10(
            Number(product.quantity_by_sale) *
              (this.salesTargetValue / this.template.sales_target),
            -2
          );
        } else {
          return null;
        }
      } else {
        return null;
      }
    },
    getValueWithoutOrder(value, orders, item) {
      if (orders.length > 0) {
        if (item.data.use_order_quantity === true) {
          value =
            value +
            Math.ceil(
              mappers.productOrderToInv(
                orders
                  .map(o => Number(o.quantity_ordered))
                  .reduce((a, b) => a + b, 0),
                item.data
              )
            );
        } else {
          value = +orders
            .map(o => Number(o.quantity_ordered))
            .reduce((a, b) => a + b, 0);
        }
      }
      return value;
    },
    getNeeded(item) {
      const orders = this.getOrders(item);
      if (this.template.sales_target !== null) {
        let invQty = this.getQuantity(item);
        const minQty = this.getMinimum(item);
        const maxQty = this.getMaximum(item);
        const saleQty = this.getBySales(item);
        if (saleQty !== null) {
          if (invQty !== null && minQty !== null && maxQty !== null) {
            if (invQty - minQty <= 0) {
              let value = round10(invQty - minQty - saleQty, -2);
              value = this.getValueWithoutOrder(value, orders, item);
              value = Math.abs(value) > maxQty ? -maxQty : value;
              value = value < 0 ? value : 0;
              return Math.floor(value);
            } else {
              return 0;
            }
          } else if (invQty !== null && minQty !== null) {
            let value = round10(invQty - minQty - saleQty, -2);
            value = this.getValueWithoutOrder(value, orders, item);
            value = value < 0 ? value : 0;
            return Math.floor(value);
          } else if (minQty !== null) {
            let value = round10(-minQty - saleQty, -2);
            value = this.getValueWithoutOrder(value, orders, item);
            return Math.floor(value);
          } else {
            return 0;
          }
        }
      }
      let invQty = this.getQuantity(item);
      const minQty = this.getMinimum(item);
      const maxQty = this.getMaximum(item);
      if (invQty !== null && minQty !== null && maxQty !== null) {
        if (invQty - minQty <= 0) {
          let value = round10(invQty - maxQty, -2);
          value = this.getValueWithoutOrder(value, orders, item);
          value = value < 0 ? value : 0;
          return Math.floor(value);
        } else {
          return 0;
        }
      } else if (invQty !== null && minQty !== null) {
        let value = round10(invQty - minQty, -2);
        value = this.getValueWithoutOrder(value, orders, item);
        value = value < 0 ? value : 0;
        return Math.floor(value);
      } else if (minQty !== null) {
        let value = -minQty;
        value = this.getValueWithoutOrder(value, orders, item);
        return Math.floor(value);
      } else {
        return 0;
      }
    },
    async saveDeliverySlip() {
      try {
        this.$store.dispatch("slo");
        const data = {
          ...this.deliverySlip,
          store: this.getStoreId,
          sales_target: this.salesTargetValue,
          number_of_days: this.numberOfDaysValue,
          date: moment(this.date)
            .hours(this.time.substr(0, 2))
            .minutes(this.time.substr(3, 2))
            .format("YYYY-MM-DDTHH:mm:ss.SSSSSSZ")
        };
        const res = await api.postDeliverySlip(data);
        if (res) {
          const tasks = [];
          this.items
            .filter(
              x => x.quantity_ordered > 0 && x.use_inventory_link === false
            )
            .forEach(x => {
              const d = {
                quantity_ordered: x.quantity_ordered,
                quantity_received: x.quantity_received,
                fk_product: x.fk_product,
                fk_delivery_slip: res.id,
                store: this.getStoreId
              };
              tasks.push(api.postDeliverySlipProduct(d));
            });
          await Promise.all(tasks);
          this.$router.push({
            name: "DeliverySlipViewer",
            params: { id: res.id }
          });
        }
      } finally {
        this.$store.dispatch("elo");
      }
    },
    inventoryLinkHandler() {
      this.items
        .filter(p => p.use_inventory_link === true)
        .forEach(p => {
          const recipe = this.getRecipes.find(
            r => r.fk_produit_intermediaire === p.id
          );
          let qtyProd = p.quantity_needed;
          if (recipe.data.use_conversion_formula) {
            qtyProd = mappers.recipeInvToProd(qtyProd, recipe, recipe.data);
          }
          const numberOfRecipe = mappers.recipeNumberOfRecipe(qtyProd, recipe);
          recipe.ingredients
            .filter(i => i.is_inventory_link === true)
            .forEach(i => {
              const item = this.items.find(x => x.id === i.pk_recette_produit);
              if (item) {
                let value = round10(i.quantite * numberOfRecipe, -2);
                if (item.data.use_conversion_formula) {
                  if (i.is_alternative_unite_mesure === true) {
                    if (
                      !mappers.isCustomUnit(i.unite_mesure) &&
                      !mappers.isCustomUnit(
                        item.data.alternative_unite_mesure_production
                      )
                    ) {
                      value = this.$units(value)
                        .from(i.unite_mesure)
                        .to(item.data.alternative_unite_mesure_production);
                    }
                  } else {
                    if (
                      !mappers.isCustomUnit(i.unite_mesure) &&
                      !mappers.isCustomUnit(item.data.unite_mesure)
                    ) {
                      value = this.$units(value)
                        .from(i.unite_mesure)
                        .to(item.data.unite_mesure);
                    }
                  }
                } else {
                  if (
                    !mappers.isCustomUnit(i.unite_mesure) &&
                    !mappers.isCustomUnit(item.data.unite_mesure_inventory)
                  ) {
                    value = this.$units(value)
                      .from(i.unite_mesure)
                      .to(item.data.unite_mesure_inventory);
                  }
                }
                item.quantity_needed += value;
                item.is_order_by_inventory_link = true;
                item.order_by_inventory_link.push({
                  recipe: recipe,
                  qty: value
                });
              }
            });
        });
      this.items
        .filter(item => item.is_order_by_inventory_link === true)
        .forEach(item => {
          let v = Math.floor(
            item.quantity_inventory - Math.abs(item.quantity_needed)
          );
          v = this.getValueWithoutOrder(v, item.orders, item);
          item.quantity_needed = v < 0 ? v : 0;
        });
    },
    setSalesTargetValue() {
      let total = 0;
      let date = moment()
        .add(1, "d")
        .format("YYYY-MM-DD");
      for (let i = 0; i < this.numberOfDaysValue; i++) {
        const estimate = this.getSalesEstimates.find(x => x.date === date);
        if (estimate) {
          total += Number(estimate.value);
        }
        date = moment(date)
          .add(1, "d")
          .format("YYYY-MM-DD");
      }
      return total;
    }
  },
  watch: {
    templateId(v) {
      if (v !== null) {
        this.template = this.getOrderTemplates.find(x => x.id === v);
        if (this.template && this.template.sales_target !== null) {
          this.numberOfDaysValue = this.template.default_number_of_days;
        }
        this.deliverySlip.fk_supplier = this.template.supplier;
      } else {
        this.template = undefined;
      }
    },
    template() {
      this.getData();
    },
    salesTargetValue() {
      this.getData();
    },
    numberOfDaysValue() {
      this.salesTargetValue = this.setSalesTargetValue();
    }
  }
};
</script>

<style></style>
